import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { cloudinaryUrl } from "../../lib/cloudinary";
import { Product } from "../../data/products";

interface ProductCardProps {
  product: Product;
}

const formatPrice = (cents: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents);

const Card = styled(Link)`
  display: block;
  color: ${({ theme }) => theme.colors.text};
`;

const Image = styled.img<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease ${({ $visible }) => ($visible ? "0.05s" : "0s")};

  z-index: ${({ $visible }) => ($visible ? 1 : 0)};
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
`;

const SingleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Name = styled.h3`
  font-family: ${({ theme }) => theme.fonts.accent};
  font-size: 1.25rem;
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
`;

const Price = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.lightText};
  letter-spacing: 0.05em;
`;

export function ProductCard({ product }: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const hasSecondImage = product.imageIds.length > 1;

  const primImage = product.imageIds[0];
  const secImage = hasSecondImage ? product.imageIds[1] : primImage;

  return (
    <Card
      to={`/collections/${product.category}/${product.slug}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ImageWrapper>
        {hasSecondImage ? (
          <>
            <Image
              src={cloudinaryUrl(primImage, { width: 600, aspectRatio: "1:1" })}
              alt={product.name}
              loading="lazy"
              $visible={!isHovered}
            />

            <Image
              src={cloudinaryUrl(secImage, { width: 600, aspectRatio: "1:1" })}
              alt={product.name}
              loading="lazy"
              $visible={isHovered}
            />
          </>
        ) : (
          <SingleImage
            src={cloudinaryUrl(primImage, { width: 600, aspectRatio: "1:1" })}
            alt={product.name}
            loading="lazy"
          />
        )}
      </ImageWrapper>
      <Name>{product.name}</Name>
      <Price>{formatPrice(product.price)}</Price>
    </Card>
  );
}

export default ProductCard;
